/**
 * Реактовая обёртка над `./ecommerce`.
 *
 * Компоненту не нужно знать ни про `sections` (категория товара), ни про
 * пользователя (сотрудникам метрику не пишем) — хук подтягивает это из стора
 * сам. Задача компонента — вызвать событие в нужный момент.
 */
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { SiteTypes } from "~/redux/reducers/paramsReducer";

import {
  ecommerceAdd,
  ecommerceClick,
  ecommerceDetail,
  ecommerceImpressions,
  ecommerceRemove,
  toEcommerceBasketItem,
  toEcommerceProduct,
} from "./ecommerce";

type ItemOptions = {
  list?: string;
  quantity?: number;
};

function noop() {}

export function useEcommerce() {
  const { sections } = useSelector((state: any) => state.sections);
  const { user } = useSelector((state: any) => state.user);
  // Коммерцию собираем только для farkop.ru. Часть компонентов (попап корзины)
  // общая для всех витрин, поэтому отсечка здесь, а не в каждом вызове.
  const { siteType } = useSelector((state: any) => state.params);
  const isEnabled = siteType === SiteTypes.Farkop;

  const map = (product: any, options: ItemOptions = {}) =>
    toEcommerceProduct(product, { sections, ...options });

  const mapBasketItem = (item: any, options: ItemOptions = {}) =>
    toEcommerceBasketItem(item, { sections, ...options });

  if (!isEnabled) {
    return {
      trackImpressions: noop,
      trackClick: noop,
      trackDetail: noop,
      trackAdd: noop,
      trackAddMany: noop,
      trackBasketRemove: noop,
      trackBasketAdd: noop,
      trackBasketRemoveMany: noop,
      sections,
      user,
    };
  }

  return {
    /** Показ списка товаров каталога. */
    trackImpressions: (products: any[] = [], list?: string) =>
      ecommerceImpressions(
        products.map((product) => map(product, { list })),
        { user },
      ),
    /** Клик по карточке товара в списке. */
    trackClick: (product: any, list?: string) =>
      ecommerceClick(map(product, { list }), { user }),
    /** Просмотр карточки товара. */
    trackDetail: (product: any, list?: string) =>
      ecommerceDetail(map(product, { list }), { user }),
    /** Добавление товара каталога в корзину. */
    trackAdd: (product: any, options: ItemOptions = {}) =>
      ecommerceAdd(map(product, { quantity: 1, ...options }), { user }),
    /** Добавление нескольких товаров одним действием (фаркоп + электрика). */
    trackAddMany: (products: any[] = [], options: ItemOptions = {}) =>
      ecommerceAdd(
        products.map((product) => map(product, { quantity: 1, ...options })),
        { user },
      ),
    /** Удаление/уменьшение количества строки корзины. */
    trackBasketRemove: (item: any, options: ItemOptions = {}) =>
      ecommerceRemove(mapBasketItem(item, options), { user }),
    /** Увеличение количества строки корзины. */
    trackBasketAdd: (item: any, options: ItemOptions = {}) =>
      ecommerceAdd(mapBasketItem(item, options), { user }),
    /** Удаление нескольких строк корзины одним действием. */
    trackBasketRemoveMany: (items: any[] = [], options: ItemOptions = {}) =>
      ecommerceRemove(
        items.map((item) => mapBasketItem(item, options)),
        { user },
      ),
    sections,
    user,
  };
}

/**
 * Отправляет `detail` один раз на товар. Переход между карточками в SPA меняет
 * `product.ID` — тогда событие уходит повторно, что и требуется.
 */
export function useEcommerceDetail(product: any, list?: string) {
  const { trackDetail } = useEcommerce();
  const sentIdRef = useRef<any>(null);

  useEffect(() => {
    if (!product?.ID || sentIdRef.current === product.ID) {
      return;
    }
    sentIdRef.current = product.ID;
    trackDetail(product, list);
  }, [product?.ID]);
}

/**
 * Отправляет `impressions` один раз на выдачу. Пагинация и фильтры меняют набор
 * id — событие уходит заново, повторный рендер тем же составом игнорируется.
 */
export function useEcommerceImpressions(products: any[], list?: string) {
  const { trackImpressions } = useEcommerce();
  const sentKeyRef = useRef<string>("");

  const key = (products || [])
    .map((product: any) => product?.ID)
    .filter(Boolean)
    .join(",");

  useEffect(() => {
    if (!key || sentKeyRef.current === key) {
      return;
    }
    sentKeyRef.current = key;
    trackImpressions(products, list);
  }, [key]);
}
